import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import { SkipErrorReporting } from '../request-options.ts';
import type { Store } from '@warp-drive/core';
import type { Employee } from '../data/resources/employee.type.ts';
import { reloadRecord } from './save-record.ts';

/**
 * Employee member actions use plural /employees/:id paths. Accounting reads pass through the
 * request manager’s demo interception.
 * Reinstate, terminate, and overrides use data.attributes; self-register preserves caller keys,
 * while dependent creation uses flat snake_case. _changelog is write-only mutation history. Generic
 * relationship actions live in their own module.
 */

const employeesUrlOptions: QueryUrlOptions = {
  identifier: { type: 'employees' },
  op: 'query',
  resourcePath: 'employees',
};

/**
 * PLURAL `/employees/{id}/...` for EVERY employee action endpoint, including
 * the accounting GETs + add-employee-dependent. The legacy adapter built those
 * via `this.buildURL('employee', id)`, which ALSO resolves to PLURAL
 * `/employees/{id}` — Ember Data pluralizes the type via the default inflector
 * (EmployeeAdapter has no pathForType override). An earlier split into a
 * "singular" helper was a misread of buildURL; the flow-diff against main
 * confirmed the wire is plural.
 */
function employeesUrl(path?: string): string {
  const base = buildBaseURL(employeesUrlOptions);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
}

/**
 * GET /employees/:id/accounting_payments
 *
 * Mirrors `EmployeeAdapter#getAccountingPaymentContributions`, which built the
 * URL via `this.buildURL('employee', employeeId) + '/accounting_payments'` and
 * sent `this.ajax(url, 'GET')`. The employee adapter consulted `demoMode`
 * first; that seam is honoured natively once routed through `store.request`.
 * The response is raw JSON (`{ payments, summary }`); the call site reads it off
 * `content`.
 */
export function accountingPaymentsRequest(employeeId: string): RequestInfo {
  return {
    url: employeesUrl(`${employeeId}/accounting_payments`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * GET /employees/:id/accounting_preview
 *
 * Mirrors `EmployeeAdapter#getAccountingFuturePayments`, which built the URL via
 * `this.buildURL('employee', employeeId) + '/accounting_preview'` and sent
 * `this.ajax(url, 'GET')`. The employee adapter consulted `demoMode` first; that
 * seam is honoured natively once routed through `store.request`. The response is
 * raw JSON (an array of preview payments); the call site reads it off `content`.
 */
export function accountingPreviewRequest(employeeId: string): RequestInfo {
  return {
    url: employeesUrl(`${employeeId}/accounting_preview`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
    // Non-critical dashboard fetch whose failures must not surface globally —
    // see SkipErrorReporting docs. All call sites catch locally.
    options: {
      [SkipErrorReporting]: true,
    },
  };
}

/**
 * GET /employees/:id/accounting_backpays
 *
 * Mirrors `EmployeeAdapter#getBackpayOptions` (admin adapter only), which built
 * the URL via `this.buildURL('employee', employeeId) + '/accounting_backpays'`
 * and sent `this.ajax(url, 'GET')`. Provided for parity completeness of the
 * accounting-GET family; its sole call site is admin-only and converts in
 * wave 3 (see module TODO). Raw JSON response; read off `content`.
 */
export function accountingBackpaysRequest(employeeId: string): RequestInfo {
  return {
    url: employeesUrl(`${employeeId}/accounting_backpays`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * GET /employees/:id/accounting_backpays — named alias of
 * {@link accountingBackpaysRequest} matching the legacy admin adapter method
 * name (`EmployeeAdapter#getBackpayOptions`). Used by the admin call site
 * (`accounting-invoices/line-items/create-dialog.gts`). Identical wire — no separate request shape.
 */
export const getBackpayOptionsRequest = accountingBackpaysRequest;

/** The transient, write-only changelog string the API records on a mutation. */
export type EmployeeChangelog = string;

/**
 * PATCH /employees/:id/reinstate
 * body `{ data: { attributes: { _changelog } } }`
 *
 * Mirrors `EmployeeAdapter#reinstateEmployee`, which built the URL via
 * `this.buildURL('employees', model.id) + '/reinstate'` and sent
 * `this.ajax(url, 'PATCH', { data: { data: { attributes: { _changelog: changelog } } } })`
 * (Ember Data's `data` option IS the request body, so the wire body is the inner
 * `{ data: { attributes: { _changelog } } }`). The legacy method then awaited
 * `model.reload()`; the call site now reloads explicitly after the request.
 */
export function reinstateEmployeeRequest(id: string, changelog?: EmployeeChangelog): RequestInfo {
  return {
    url: employeesUrl(`${id}/reinstate`),
    method: 'PATCH',
    body: JSON.stringify({ data: { attributes: { _changelog: changelog } } }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Terminates an employee via DELETE with _changelog under data.attributes. syf_grace_acknowledged
 * is omitted unless provided; callers must reload the record afterward.
 */
export function terminateEmployeeRequest(
  id: string,
  changelog?: EmployeeChangelog,
  syfGraceAcknowledged?: boolean
): RequestInfo {
  const attributes: { _changelog?: EmployeeChangelog; syf_grace_acknowledged?: boolean } = {
    _changelog: changelog,
  };
  if (syfGraceAcknowledged !== undefined) {
    attributes.syf_grace_acknowledged = syfGraceAcknowledged;
  }
  return {
    url: employeesUrl(id),
    method: 'DELETE',
    body: JSON.stringify({ data: { attributes } }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /employees/:id/update-overrides
 * body `{ data: { attributes: { overrides: [override], _changelog } } }`
 *
 * Mirrors `EmployeeAdapter#updateOverrides`, which built the URL via
 * `this.buildURL('employees', employeeId) + '/update-overrides'` and sent
 * `this.ajax(url, 'PATCH', { data: { data: { attributes: { overrides: [overrides], _changelog: changelog } } } })`
 * — note the SINGLE override object is wrapped in a one-element array, preserved
 * exactly here.
 */
export function updateOverridesRequest(
  id: string,
  override: Record<string, unknown>,
  changelog: EmployeeChangelog
): RequestInfo {
  return {
    url: employeesUrl(`${id}/update-overrides`),
    method: 'PATCH',
    body: JSON.stringify({
      data: { attributes: { overrides: [override], _changelog: changelog } },
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * POST /employees/self-register  (PLURAL, no id)
 *
 * Mirrors `EmployeeAdapter#selfRegister`, which built the URL via
 * `this.buildURL('employees') + '/self-register'` and sent
 * `this.ajax(url, 'POST', { data })` — the `data` object is sent verbatim as the
 * request body. The body keys are exactly whatever the call site passes; the
 * sole call site sends `{ email, first_name, last_name }`. The body shape is
 * therefore parameterized (not hard-coded) so the wire stays byte-identical to
 * whatever the caller hands in.
 */
export function selfRegisterRequest(data: Record<string, unknown>): RequestInfo {
  return {
    url: employeesUrl('self-register'),
    method: 'POST',
    body: JSON.stringify(data),
    cacheOptions: { [SkipCache]: true },
  };
}

/** The flat snake_case body for `add-employee-dependent`. */
export interface AddEmployeeDependentBody {
  first_name: string;
  last_name: string;
  email: string;
  birth_date: string;
  relationship: string;
}

/**
 * POST /employee/:id/add-employee-dependent
 * body `{ first_name, last_name, email, birth_date, relationship }` (snake_case)
 *
 * Mirrors `EmployeeAdapter#addEmployeeDependent`, which built the URL via
 * `this.buildURL('employee', employeeId) + '/add-employee-dependent'`
 * and sent
 * `this.ajax(url, 'POST', { data: { first_name, last_name, email, birth_date, relationship } })`.
 * The snake_case key casing is preserved EXACTLY.
 */
export function addEmployeeDependentRequest(
  employeeId: string,
  data: AddEmployeeDependentBody
): RequestInfo {
  return {
    url: employeesUrl(`${employeeId}/add-employee-dependent`),
    method: 'POST',
    body: JSON.stringify({
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      birth_date: data.birth_date,
      relationship: data.relationship,
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /employees/:id/create_invite  (admin-only surface)
 *
 * Mirrors `EmployeeAdapter#createInvite` (tio-admin adapter), which built the URL
 * via `this.buildURL('employees', employeeId) + '/create_invite'` and sent
 * `this.ajax(url, 'PATCH')` with NO body. No request body is emitted here either.
 */
export function createInviteRequest(employeeId: string): RequestInfo {
  return {
    url: employeesUrl(`${employeeId}/create_invite`),
    method: 'PATCH',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /employees/:id/invite  (admin-only surface)
 *
 * Mirrors `EmployeeAdapter#sendInviteEmail` (tio-admin adapter), which built the
 * URL via `this.buildURL('employees', employeeId) + '/invite'` and sent
 * `this.ajax(url, 'PATCH')` with NO body. No request body is emitted here either.
 */
export function sendInviteEmailRequest(employeeId: string): RequestInfo {
  return {
    url: employeesUrl(`${employeeId}/invite`),
    method: 'PATCH',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /employees/:id/clear-employee-hash  (admin-only surface, NO body)
 *
 * Mirrors `EmployeeAdapter#clearEmployeeHash` (tio-admin adapter), which built
 * the URL via `this.buildURL('employees', employeeId) + '/clear-employee-hash'`
 * and sent `this.ajax(url, 'PATCH')` with NO body. No request body is emitted
 * here either.
 *
 * NOTE (route): `clear-employee-hash` is NOT present in the tio-api
 * `config/routes.rb` snapshot in this workspace (the `:employees` member block at
 * config/routes.rb:296-310 wires reinstate/invite/create_invite/etc but not this
 * one). The wire is taken from the LEGACY ADAPTER (authoritative for what the FE
 * emits; WarpDrive parity is byte-identical-to-adapter). Flagged for the API-side
 * owner to confirm the route exists.
 */
export function clearEmployeeHashRequest(employeeId: string): RequestInfo {
  return {
    url: employeesUrl(`${employeeId}/clear-employee-hash`),
    method: 'PATCH',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Terminates an employee and reloads the record because the DELETE response is empty. The explicit
 * store supports owner-free resource callers.
 */
export async function terminateEmployee(store: Store, employee: Employee): Promise<Employee> {
  await store.request(terminateEmployeeRequest(employee.id as string));
  return reloadRecord(store, employee);
}

/**
 * Reinstates an employee and reloads the record because the PATCH response is empty. The explicit
 * store supports owner-free resource callers.
 */
export async function reinstateEmployee(store: Store, employee: Employee): Promise<Employee> {
  await store.request(reinstateEmployeeRequest(employee.id as string));
  return reloadRecord(store, employee);
}
